// src/hooks/useAyahPlayer.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSignedUrl } from '../api/quranService';

const urlCache = new Map(); // media key -> playable URL
const resolveUrl = async (key) => {
  if (!urlCache.has(key)) urlCache.set(key, await getSignedUrl(key));
  return urlCache.get(key);
};

/**
 * One audio player for a reading page. Items are {id, label, mediaKey}; playing an item from a
 * queue carries on through the rest of the queue, like reading along.
 */
const useAyahPlayer = () => {
  const audio = useMemo(() => new Audio(), []);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [status, setStatus] = useState('idle'); // idle | loading | playing | paused | error
  // Event listeners are attached once, so they read the latest queue through this ref
  const latest = useRef({ queue, index });
  latest.current = { queue, index };

  const current = index >= 0 ? queue[index] || null : null;
  const currentId = current?.id || null;

  useEffect(() => {
    const onPlaying = () => setStatus('playing');
    const onPause = () => !audio.ended && setStatus((s) => (s === 'loading' ? s : 'paused'));
    const onEnded = () => {
      const { queue: q, index: i } = latest.current;
      if (i + 1 < q.length) {
        setIndex(i + 1);
      } else {
        setIndex(-1);
        setStatus('idle');
      }
    };
    const onError = () => audio.getAttribute('src') && setStatus('error');
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
    };
  }, [audio]);

  // Load and play whenever a different item becomes current
  useEffect(() => {
    if (!currentId) return undefined;
    const item = latest.current.queue[latest.current.index];
    let cancelled = false;
    setStatus('loading');
    resolveUrl(item.mediaKey)
      .then((url) => {
        if (cancelled) return undefined;
        audio.src = url;
        return audio.play();
      })
      .catch(() => !cancelled && setStatus('error'));
    return () => { cancelled = true; };
  }, [currentId, audio]);

  const toggle = useCallback(() => {
    if (!latest.current.queue[latest.current.index]) return;
    if (audio.paused) audio.play().catch(() => setStatus('error'));
    else audio.pause();
  }, [audio]);

  /** Play items[start], then carry on through the rest. Pressing the current item pauses/resumes it. */
  const playFrom = useCallback((items, start = 0) => {
    const { queue: q, index: i } = latest.current;
    if (q[i] && items[start] && q[i].id === items[start].id && status !== 'error') {
      toggle();
      return;
    }
    setQueue(items);
    setIndex(start);
  }, [status, toggle]);

  const skip = useCallback((step) => {
    const { queue: q, index: i } = latest.current;
    const next = i + step;
    if (next >= 0 && next < q.length) setIndex(next);
  }, []);

  const stop = useCallback(() => {
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    setIndex(-1);
    setStatus('idle');
  }, [audio]);

  return {
    current,
    status,
    hasPrev: index > 0,
    hasNext: index >= 0 && index + 1 < queue.length,
    isActive: (id) => currentId === id,
    isPlaying: (id) => currentId === id && (status === 'playing' || status === 'loading'),
    playFrom,
    toggle,
    next: () => skip(1),
    prev: () => skip(-1),
    stop,
  };
};

export default useAyahPlayer;
